import type { RouterLinkProps } from '@blockit/web-shared/ui/routerLink';
import { Link, type LinkProps } from '@tanstack/react-router';
import type { FC } from 'react';

/** Resolves the relative href; never sent anywhere. */
const PARSE_BASE = 'http://app.invalid';

/**
 * A client-side link to an in-app href built at runtime (`/threads/${id}?archive=open`). Route
 * literals should use `Link` directly so the router type-checks them; this is for hrefs composed
 * by helpers, and is what `RouterLinkProvider` hands the shared `@blockit/web-shared` components.
 */
interface HrefLinkProps extends RouterLinkProps {
  target?: string;
  rel?: string;
  replace?: boolean;
}

export const HrefLink: FC<HrefLinkProps> = ({ to, ...props }) => {
  const url = new URL(to, PARSE_BASE);
  return (
    <Link
      // The router validates the path when it matches; the type only knows route literals.
      to={url.pathname as LinkProps['to']}
      search={Object.fromEntries(url.searchParams) as LinkProps['search']}
      hash={url.hash ? url.hash.slice(1) : undefined}
      // Active on the path and below it, whatever the filters: `aria-current` styles the nav.
      activeOptions={{ includeSearch: false }}
      {...props}
    />
  );
};
